import { SUPABASE_URL } from "./config";

/**
 * Owner session model.
 *
 * Two authentication modes are supported. They differ in WHO identifies
 * the tenant and HOW the token is presented to the edge function.
 *
 * 1. **Bearer mode (production).** The session carries a per-campground
 *    admin token (plaintext). The edge function hashes it and resolves
 *    the campground by `admin_token_hash`. The token alone is enough —
 *    `publicKey` and `slug` are only kept around for display.
 *
 *    Sessions in this mode are bootstrapped through the `/handoff` route,
 *    which reads the token from the URL fragment Roverpass redirected to.
 *
 * 2. **Internal mode (dev / support).** The session carries the platform
 *    INTERNAL_SYNC_TOKEN plus a tenant identifier (`publicKey` or `slug`)
 *    in the body. This is the only way to authenticate before a
 *    campground has a per-campground admin token provisioned.
 *
 *    Use this for local development by setting `VITE_DEV_BYPASS_TOKEN`
 *    in `dashboard/.env.local`. It's also the path the legacy Login
 *    form takes when an owner is manually pasting the platform token.
 *
 * Lifecycle
 *   - Sessions live in `localStorage` so a refresh stays signed in.
 *   - Switching campgrounds (or signing out) clears the entry.
 *   - The `/handoff` route always overwrites whatever was there — the
 *     URL is the source of truth at handoff time.
 */

const STORAGE_KEY = "roverpass-chatbot-dashboard";

export type SessionMode = "bearer" | "internal";

export interface Session {
  url:        string;        // Supabase project URL
  token:      string;        // plaintext bearer token OR INTERNAL_SYNC_TOKEN
  mode:       SessionMode;
  publicKey?: string;        // optional display + identity (internal mode)
  slug?:      string;        // optional display + identity (internal mode)
}

// ---------------------------------------------------------------------------
// Dev-only env fallback. When VITE_DEV_BYPASS_TOKEN is set, the dashboard
// boots straight into an internal-mode session against the configured
// campground. Useful for local iteration without going through Roverpass.
// ---------------------------------------------------------------------------
function loadBypassSession(): Session | null {
  const token = import.meta.env.VITE_DEV_BYPASS_TOKEN;
  if (!token) return null;
  const slug = import.meta.env.VITE_DEV_SLUG;
  if (!slug) return null;
  return {
    url:   import.meta.env.VITE_DEV_SUPABASE_URL || SUPABASE_URL,
    slug,
    token,
    mode: "internal",
  };
}

export function isBypassActive(): boolean {
  return loadBypassSession() !== null;
}

export function loadSession(): Session | null {
  const bypass = loadBypassSession();
  if (bypass) return bypass;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (!parsed.url || !parsed.token) return null;
    // Migrate legacy sessions that don't carry an explicit mode. The old
    // shape always included either publicKey or slug — that means internal.
    const mode: SessionMode = parsed.mode === "bearer" ? "bearer" : "internal";
    if (mode === "internal" && !parsed.publicKey && !parsed.slug) return null;
    return {
      url:       parsed.url,
      token:     parsed.token,
      mode,
      publicKey: parsed.publicKey,
      slug:      parsed.slug,
    };
  } catch {
    return null;
  }
}

export function saveSession(s: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}
